// InterventionsTerrain — Visite Travaux · Recherche de fuite · Rapports de visite
//
// 🔑 UN SEUL composant pour les TROIS entrées de menu, paramétré par `type`.
//    ⛔ Pas trois écrans recopiés : la divergence entre copies est la cause-racine
//    de la majorité des régressions (CLAUDE.md, « une logique = une fonction »).
//
// 🔴 RÉÉCRIT LE 2026-09-28 (N°734 mesuré, N°773 point 3 construit) — LES TROIS
//    GRILLES ÉTAIENT VIDES À L'ÉCRAN, et voici pourquoi, mesuré :
//    elles filtraient `metier_dossiers` sur `type_intervention`, et **AUCUN dossier
//    ne porte ce champ** (0 en PROD, 0 en PREPROD). Le filtre était donc toujours
//    faux. ⚠️ Le code était « présent », l'effet NUL — et l'écran invitait même
//    l'utilisateur à « marquer un dossier », ce qu'aucun écran ⛔ ne permet de faire.
//
// 🔑 LA VRAIE DONNÉE EXISTE AILLEURS : `visite_saisies`, alimentée par le Visit
//    Engine (11 visites pour `mario_bat` en preprod au 2026-09-28). On lit donc CE
//    QUE LE PRODUIT ÉCRIT, ⛔ pas un champ que personne ne remplit.
//
// CE QUI EST RÉUTILISÉ, ⛔ pas réécrit — le motif est copié de « Devis & Factures »
// (`DevisFacturesPage.jsx`) : UN endpoint qui liste tout le tenant → `OHGrid` →
// chaque ligne OUVRE SON DOSSIER → le document s'ouvre depuis la grille.
//   · `OHGrid` — tri, recherche, actions par ligne, pagination.
//   · `GET /api/visite/saisies?toutes=true` — route EXISTANTE, scopée tenant
//     (DEC-091). ⛔ Aucun endpoint nouveau.
//   · `openAuthedPdf` + `getAuthHeaders` (`dossier/helpers`) — SOURCE UNIQUE (N°673) :
//     un rapport se DEMANDE avec l'en-tête, il ⛔ ne se NAVIGUE pas (un `<a href>`
//     perd la session et rend 401).
//   · `formatDateFR` — formatage unique de l'application.
//
// ⚠️ `toutes=true` EST INDISPENSABLE : sans ce drapeau la route ⛔ ne rend QUE les
//    brouillons, et les visites CLÔTURÉES — les seules à avoir un rapport définitif —
//    seraient invisibles. C'est exactement ce dont la vue « Rapports » dépend.
// ⚠️ LIMITE SERVEUR ASSUMÉE, ⛔ pas masquée : la route fait `.to_list(50)`. Au-delà,
//    la liste est tronquée EN AMONT et la pagination ⛔ n'y change rien : la bannière
//    le dit plutôt que de laisser croire à un parc complet.
import { useState, useEffect, useCallback, useMemo } from "react";
import { Hammer, Droplets, FileText, ExternalLink, AlertTriangle } from "lucide-react";
import { OHGrid } from "../../components/ui/oh-grid";
import { formatDateFR } from "../../utils/dateFormat";
// 🔑 SOURCE UNIQUE (N°673) — ⛔ ne PAS refabriquer l'ouverture d'un PDF authentifié.
import { getAuthHeaders, openAuthedPdf } from "./dossier/helpers";
// 🔴 N°1016-B — les visites de CHANTIER (relevés) : la MÊME donnée que l'écran terrain.
import { visitesDuTenant } from "./artisan/api";

const API = process.env.REACT_APP_BACKEND_URL;

// ⚠️ La route fait `.to_list(50)` : au-delà, c'est le SERVEUR qui tronque.
const LIMITE_SERVEUR = 50;

//: États d'une saisie de visite, tels que le backend les écrit (`visite/saisie.py`).
const ETATS = {
  brouillon: { libelle: "En cours", couleur: "amber" },
  terminee: { libelle: "Clôturée", couleur: "green" },
  abandonnee: { libelle: "Abandonnée", couleur: "red" },
};

// 🔑 CHAQUE VUE EST UN FILTRE SUR LA MÊME DONNÉE, ⛔ pas une grille séparée : deux
//    grilles sur le même sujet divergent toujours (CLAUDE.md, « une logique = une
//    fonction »). `selection` dit CE QUE la vue garde, et rien d'autre ne change.
// ⚠️ Le filtre porte sur `profil_code` — la donnée que le Visit Engine écrit
//    RÉELLEMENT — et par PRÉFIXE, parce que les profils de démonstration existent
//    (mesuré : `visite_travaux` ET `visite_travaux_demo` chez mario_bat).
export const TYPES_INTERVENTION = {
  visite_travaux: {
    titre: "Visite Travaux",
    icone: Hammer,
    sousTitre: "Visites de chantier : constat, métré, éléments pour le devis.",
    // 🔴 N°1016-B — les relevés de chantier (Visite Rénovation) sont des visites de travaux : ils
    //    vivent dans `chantier_releves`, ⛔ pas dans `visite_saisies`, et la grille ne les lisait pas.
    selection: (v) => v.source === "releve" || String(v.profil_code || "").startsWith("visite_travaux"),
    vide: "Aucune visite de chantier enregistrée. Une visite s'ouvre depuis un dossier, onglet Visites.",
  },
  recherche_fuite: {
    titre: "Recherche de fuite",
    icone: Droplets,
    sousTitre: "Interventions de recherche de fuite et de diagnostic.",
    selection: (v) => String(v.profil_code || "").startsWith("recherche_fuite"),
    vide: "Aucune recherche de fuite enregistrée.",
  },
  rapports_visite: {
    titre: "Rapports de visite",
    icone: FileText,
    sousTitre: "Comptes rendus des visites clôturées.",
    // ⚠️ SEULES les visites CLÔTURÉES ont un rapport définitif. Lister les
    //    brouillons ici promettrait un document qui ⛔ n'existe pas encore.
    selection: (v) => v.statut === "terminee",
    vide: "Aucune visite clôturée : un rapport définitif suppose une visite terminée.",
  },
};

/** Un relevé de chantier, mis à la forme d'une ligne de la grille. 🔑 Traduction d'AFFICHAGE
 *  seulement : en cours ⇒ « En cours », clos ⇒ « Clôturée ». Rien n'est écrit nulle part. */
export function releveEnLigne(v) {
  return {
    id: v.id, source: "releve", dossier_id: v.dossier_id, dossier_reference: v.dossier_reference,
    profil_code: v.profil_code,
    statut: v.etat === "en_cours" ? "brouillon" : v.etat === "clos" ? "terminee" : "abandonnee",
    lieu: v.lieu, nombre_postes: v.nombre_postes, devis_numero: v.devis_numero,
    cree_le: formatDateFR(v.ouvert_le), cloture_le: v.clos_le ? formatDateFR(v.clos_le) : "",
  };
}

function BadgeEtat({ statut }) {
  const e = ETATS[statut] || { libelle: statut || "—", couleur: "blue" };
  return (
    <span className="text-xs px-1.5 py-0.5 rounded"
          style={{ background: `var(--oh-stat-${e.couleur}-bg)`,
                   color: `var(--oh-stat-${e.couleur}-text)` }}>
      {e.libelle}
    </span>
  );
}

export default function InterventionsTerrain({ type = "visite_travaux", onOpenDossier, tenantId }) {
  const conf = TYPES_INTERVENTION[type] || TYPES_INTERVENTION.visite_travaux;
  const Icone = conf.icone;
  const [visites, setVisites] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [erreurRapport, setErreurRapport] = useState(null);

  const [nbSaisies, setNbSaisies] = useState(0);
  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    // 🔴 N°1016-B — DEUX sources, UNE grille : `visite_saisies` (Visit Engine) ET `chantier_releves`
    //    (Visite Rénovation). ⛔ Aucune copie de l'une dans l'autre : une visite n'a qu'une vérité.
    //    Chaque source échoue SÉPARÉMENT et le DIT : une seule panne ⛔ ne vide pas l'autre.
    const [saisies, releves] = await Promise.allSettled([
      (async () => {
        // ⚠️ `toutes=true` : sinon ⛔ QUE les brouillons (cf. en-tête du fichier).
        const r = await fetch(`${API}/api/visite/saisies?toutes=true`,
                              { headers: getAuthHeaders(tenantId), credentials: "include" });
        if (!r.ok) {
          const c = await r.json().catch(() => ({}));
          throw new Error(c.detail || `visites HTTP ${r.status}`);
        }
        const d = await r.json();
        // ⚠️ On lit CE QUE le serveur renvoie (`{tenant, en_cours: [...]}`) : ⛔ on ⛔ ne
        //    suppose PAS un tableau nu, sinon l'écran est vide sans jamais le dire.
        return Array.isArray(d) ? d : (d.en_cours || d.saisies || []);
      })(),
      visitesDuTenant(),
    ]);
    const lignesSaisies = saisies.status === "fulfilled" ? saisies.value : [];
    const lignesReleves = releves.status === "fulfilled" ? (releves.value.visites || []).map(releveEnLigne) : [];
    setNbSaisies(lignesSaisies.length);
    setVisites([
      ...lignesSaisies.map((v) => ({
        ...v,
        id: v.id,
        cree_le: formatDateFR(v.created_at),
        cloture_le: v.cloturee_le ? formatDateFR(v.cloturee_le) : "",
      })),
      ...lignesReleves,
    ]);
    const pannes = [saisies, releves].filter((x) => x.status === "rejected")
      .map((x) => x.reason?.message || "Chargement impossible");
    setErreur(pannes.length ? pannes.join(" · ") : null);
    setChargement(false);
  }, [tenantId]);

  useEffect(() => { charger(); }, [charger]);

  // 🔑 Le filtre de la VUE, appliqué à la donnée commune.
  const lignes = useMemo(() => visites.filter(conf.selection), [visites, conf]);
  const tronque = nbSaisies >= LIMITE_SERVEUR;

  // 🔑 SOURCE UNIQUE (N°673) — le rapport se DEMANDE avec l'en-tête.
  const ouvrirRapport = (ligne) => {
    setErreurRapport(null);
    return openAuthedPdf({
      url: `${API}/api/visite/saisies/${ligne.id}/rapport`,
      headers: getAuthHeaders(tenantId),
      onError: (msg, statut) => setErreurRapport(
        (statut === 401 || statut === 403)
          ? "Session expirée : reconnectez-vous pour ouvrir le rapport."
          : msg),
    });
  };

  const colonnes = useMemo(() => [
    {
      key: "dossier_reference", label: "Dossier", mono: true, sortable: true,
      render: (v, row) => v || row.dossier_id?.slice(-6) || "—",
    },
    { key: "profil_code", label: "Type de visite", sortable: true },
    {
      key: "statut", label: "État", sortable: true,
      render: (v) => <BadgeEtat statut={v} />,
    },
    { key: "lieu", label: "Lieu", sortable: true, render: (v) => v || "—" },
    { key: "nombre_postes", label: "Lignes", sortable: true, render: (v) => (v == null ? "—" : v) },
    { key: "cree_le", label: "Ouverte le", muted: true, sortable: true },
    { key: "cloture_le", label: "Clôturée le", muted: true, sortable: true },
    {
      key: "devis_numero", label: "Devis", sortable: true,
      render: (v) => v || "—",
    },
  ], []);

  const actions = useMemo(() => ([
    {
      // ⚠️ `row.id` est l'id de la SAISIE, ⛔ pas du dossier : c'est `dossier_id`
      //    qu'il faut ouvrir. Confondre les deux ouvrirait un dossier inexistant.
      label: "Ouvrir le dossier",
      icon: ExternalLink,
      onClick: (row) => row.dossier_id && onOpenDossier?.(row.dossier_id),
    },
    {
      // 🔑 LE RAPPORT EST ACCESSIBLE DEPUIS LA GRILLE — exigence explicite du N°773.
      // ⚠️ Masqué sur une visite ABANDONNÉE : proposer un document qui n'existe pas
      //    apprend à l'utilisateur à ne plus cliquer.
      label: "Rapport PDF",
      icon: FileText,
      onClick: (row) => ouvrirRapport(row),
      // ⚠️ Un relevé de chantier n'a PAS de rapport PDF : son livrable est le devis.
      hidden: (row) => row.statut === "abandonnee" || row.source === "releve",
    },
  ]), [onOpenDossier]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div
          className="p-2 rounded-lg"
          style={{ background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" }}
        >
          <Icone className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
            {conf.titre}
          </h1>
          <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            {conf.sousTitre}
          </p>
        </div>
      </div>

      {/* ⚠️ L'état est dit AVANT la grille, ⛔ pas caché dans un coin : l'utilisateur
          doit comprendre ce qu'il voit ET ce qui viendra. */}
      <div
        className="rounded-lg p-3 text-sm"
        style={{
          background: "var(--oh-info-bg)",
          color: "var(--oh-info-text)",
          border: "0.5px solid var(--oh-info-border)",
        }}
        data-testid={`interventions-etat-${type}`}
      >
        Cette vue liste les dossiers de votre organisation marqués «&nbsp;{conf.titre}&nbsp;».
        Une intervention se crée depuis un dossier. {conf.aVenir}
      </div>

      {erreur && (
        <div
          className="rounded-lg p-3 text-sm"
          style={{
            background: "var(--oh-error-bg)",
            color: "var(--oh-error-text)",
            border: "0.5px solid var(--oh-error-border)",
          }}
        >
          {erreur}
        </div>
      )}

      <OHGrid
        data={lignes}
        columns={colonnes}
        rowActions={actions}
        onRowClick={(row) => onOpenDossier?.(row.id)}
        loading={chargement}
        onRefresh={charger}
        selectable
        defaultSortKey="created_at"
        defaultSortDir="desc"
        searchPlaceholder={`Rechercher une ${conf.titre.toLowerCase()}…`}
        emptyMessage={`Aucune ${conf.titre.toLowerCase()} enregistrée pour le moment. Marquez un dossier comme « ${conf.titre} » pour le voir apparaître ici.`}
        testIdPrefix={`interventions-${type}`}
      />
    </div>
  );
}
